import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { uint8ArrayToBase64, base64ToUint8Array } from '@/utils/yjs';
import { Logger } from '@/utils/logger';

const logger = Logger.getLoggerWithPrefix('YjsAwarenessBroadcaster');

/**
 * Broadcasts Yjs document updates through awareness channel for instant sync.
 *
 * This is the SINGLE point where we intercept changes for instant sync:
 * - All commands write to Yjs
 * - Yjs emits 'update' events
 * - We broadcast those updates via awareness
 * - Other clients apply them directly to their Yjs doc
 * - Projection handles Yjs → Store sync
 *
 * No manual operation tracking, no format transformations, no patchy broadcasting.
 */
export class YjsAwarenessBroadcaster {
  private doc: Y.Doc;
  private awareness: Awareness;
  private recentUpdates: Array<{ data: Uint8Array; timestamp: number }> = [];
  private maxRecentUpdates = 10;
  private cleanupInterval: number | null = null;
  private updateHandler: ((update: Uint8Array, origin: any, doc: Y.Doc) => void) | null = null;

  constructor(doc: Y.Doc, awareness: Awareness) {
    this.doc = doc;
    this.awareness = awareness;

    // Clean up old updates every 5 seconds
    this.cleanupInterval = window.setInterval(() => {
      this.cleanupOldUpdates();
    }, 5000);

    this.setupListener();
  }

  private setupListener() {
    // Listen to Yjs doc updates
    this.updateHandler = (update: Uint8Array, origin: any) => {
      // Don't broadcast if this update came from:
      // 1. Electric (already synced via shape stream)
      // 2. Awareness (would create infinite loop)
      // 3. Server snapshot
      if (origin === 'electric' || origin === 'awareness' || origin === 'server' || origin === 'broadcast') {
        return;
      }

      // Log to track what operations are being broadcast
      logger.info('Detected local change:', {
        origin,
        updateSize: update.length,
        shapesCount: this.doc.getMap('shapes').size,
        connectorsCount: this.doc.getMap('connectors').size
      });

      // This is a local change - broadcast it via awareness for instant sync
      this.broadcastUpdate(update);
    };

    this.doc.on('update', this.updateHandler);
  }

  private broadcastUpdate(update: Uint8Array) {
    const timestamp = Date.now();

    // Store recent updates locally
    this.recentUpdates.push({ data: update, timestamp });
    if (this.recentUpdates.length > this.maxRecentUpdates) {
      this.recentUpdates = this.recentUpdates.slice(-this.maxRecentUpdates);
    }

    // Get current queue from awareness (or initialize empty)
    const currentState = this.awareness.getLocalState();
    const currentQueue = currentState?.yjsUpdateQueue || [];

    // Append new update to queue (keep last 10)
    const newQueue = [
      ...currentQueue,
      {
        data: uint8ArrayToBase64(update), // Base64 string for compact JSON transport
        timestamp,
        clientId: this.doc.clientID,
      }
    ].slice(-10); // Keep last 10 updates

    // Broadcast queue via awareness
    this.awareness.setLocalStateField('yjsUpdateQueue', newQueue);
  }

  /**
   * Get recent updates from all clients (for catch-up)
   */
  getRemoteUpdates(): Array<{ data: Uint8Array; timestamp: number; clientId: number }> {
    const updates: Array<{ data: Uint8Array; timestamp: number; clientId: number }> = [];
    const states = Array.from(this.awareness.getStates().entries()) as [number, any][];

    for (const [clientId, state] of states) {
      // Skip our own updates
      if (clientId === this.awareness.clientID) continue;

      // Get the update queue from this client
      const updateQueue = state?.yjsUpdateQueue;
      if (Array.isArray(updateQueue)) {
        for (const updateData of updateQueue) {
          if (updateData?.data && updateData?.timestamp) {
            updates.push({
              data: typeof updateData.data === 'string'
                ? base64ToUint8Array(updateData.data)
                : new Uint8Array(updateData.data),
              timestamp: updateData.timestamp,
              clientId: updateData.clientId || clientId,
            });
          }
        }
      }
    }

    // Sort by timestamp (oldest first) to maintain causality
    return updates.sort((a, b) => a.timestamp - b.timestamp);
  }

  private cleanupOldUpdates() {
    const now = Date.now();
    const maxAge = 10000; // 10 seconds

    this.recentUpdates = this.recentUpdates.filter((u) => now - u.timestamp < maxAge);
  }

  destroy() {
    if (this.updateHandler) {
      this.doc.off('update', this.updateHandler);
      this.updateHandler = null;
    }

    if (this.cleanupInterval !== null) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // Clear awareness field
    this.awareness.setLocalStateField('yjsUpdateQueue', null);
  }
}
